import {
  Object3D,
  Vector2,
  Vector3,
  Mesh,
  ExtrudeGeometry,
  Shape,
  Box3,
  Group,
  Texture,
  Raycaster,
  MathUtils,
  Vector3Like,
  ShapeGeometry,
  Vector2Like,
} from 'three'
import Util from '~/components/DrawingCanvas/lib/util'
import Units from '~/components/DrawingCanvas/lib/units'
import { FormattedProducts } from './formatProducts'
import type { Vector } from './types'
import type { Ceiling, Obstruction, Wall, WallSegment } from '~/store/objects/types'
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js'
import {
  modelToUI,
  vectorModelToUI,
  UNIT_CONVERSION_SCALAR,
  objectUIToModel,
} from '~/components/DrawingCanvas/util/units'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { cornerPoints } from '~/components/DrawingCanvas/util/boxDragUtil'
import { lruMemoize } from 'reselect'
import LayerKeys from '~/config/layerKeys'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { denormalizeWalls } from '~/components/DrawingCanvas/lib/collisionMeshes'
import { getInteriorWallFullHeight, getPerimeterPoints } from '~/components/DrawingCanvas/Walls/util'

const getWallClones = (segments: Record<string, WallSegment>, walls: Record<string, Wall>) => {
  const denormalizedWalls = denormalizeWalls(segments, walls)
  const denormalizedSegments = denormalizedWalls.map(wall => wall.segments).flat()
  const interiorWallFullHeight = getInteriorWallFullHeight(segments)
  return denormalizedSegments.map(segment => {
    const shape = new Shape()
      .moveTo(modelToUI(segment.insetEdge.start.x), modelToUI(segment.insetEdge.start.y))
      .lineTo(modelToUI(segment.insetEdge.end.x), modelToUI(segment.insetEdge.end.y))
      .lineTo(modelToUI(segment.outsetEdge.end.x), modelToUI(segment.outsetEdge.end.y))
      .lineTo(modelToUI(segment.outsetEdge.start.x), modelToUI(segment.outsetEdge.start.y))
      .closePath()

    const wall = new Mesh(
      new ExtrudeGeometry(shape, {
        steps: 1,
        depth: modelToUI(segment.isFullHeight ? interiorWallFullHeight : segment.height),
        bevelEnabled: false,
      })
    )
    wall.name = `wall segment ${segment.id}`
    return wall
  })
}

function isPointInPolygon(point: Vector2Like, polygonPoints: Vector2Like[]) {
  // ray-casting algorithm based on
  // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
  const x = point.x
  const y = point.y

  let inside = false
  let i = 0
  for (let j = polygonPoints.length - 1; i < polygonPoints.length; j = i - 1) {
    const xi = polygonPoints[i].x
    const yi = polygonPoints[i].y
    const xj = polygonPoints[j].x
    const yj = polygonPoints[j].y

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside

    i += 1
  }

  return inside
}

function getSimulationPoints(
  segments: Record<string, WallSegment>,
  walls: Record<string, Wall>,
  productPositions: FormattedProducts
) {
  const wallPerimeters = denormalizeWalls(segments, walls).map(({ segments }) => getPerimeterPoints(segments))
  wallPerimeters.sort((wallA, wallB) => Util.polygonArea(wallB) - Util.polygonArea(wallA))
  const encounteredProducts = new Set<string>()
  return wallPerimeters.reduce<Vector3Like[]>((locations, perimeterPoints) => {
    const firstContainedProduct = productPositions.find(({ id, positionData }) => {
      if (encounteredProducts.has(id)) return false
      return isPointInPolygon(new Vector3().copy(positionData), perimeterPoints)
    })
    if (firstContainedProduct !== undefined) {
      encounteredProducts.add(firstContainedProduct.id)
      locations.push(objectUIToModel(firstContainedProduct.unscaledPositionData))
    }
    return locations
  }, [])
}

function approximatelyTheSame(a: Vector3Like, b: Vector3Like, epsilon: number = 0.1) {
  return Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.z - b.z) < epsilon
}

const getFloorClones = (segments: Record<string, WallSegment>, walls: Record<string, Wall>) => {
  return Object.values(walls)
    .filter(wall => wall.layerKey === LayerKeys.EXTERIOR_WALLS)
    .filter(wall =>
      approximatelyTheSame(
        segments[wall.segments[0]].startPoint,
        segments[wall.segments[wall.segments.length - 1]].endPoint
      )
    )
    .map(wall => {
      const points = wall.segments.map(segment => segments[segment].startPoint)
      points.push(segments[wall.segments[wall.segments.length - 1]].endPoint)

      const shape = new Shape()
      shape.moveTo(modelToUI(points[0].x), modelToUI(points[0].y))
      for (const point of points.slice(1, points.length - 1)) {
        shape.lineTo(modelToUI(point.x), modelToUI(point.y))
      }
      const end = segments[wall.segments[wall.segments.length - 1]].endPoint
      shape.lineTo(modelToUI(end.x), modelToUI(end.y))
      shape.closePath()

      const floor = new Mesh(new ShapeGeometry(shape))

      floor.name = `floor for ${wall.id}`

      return floor
    })
}

const getCeilingClones = (ceilings: Record<string, Ceiling>) => {
  return Object.values(ceilings).reduce<Object3D[]>((accumulator, current) => {
    if (!current.enabled) return accumulator
    const geometry = new ConvexGeometry(
      current.perimeterPoints.map(pt => new Vector3(pt.x, pt.y, modelToUI(current.height)))
    )
    const mesh = new Mesh(geometry)
    mesh.name = `ceiling ${current.id}`
    accumulator.push(mesh)
    return accumulator
  }, [])
}

function basicObstructionMesh(obstruction: Obstruction, roofs: Mesh[]) {
  const points = obstruction.positions.map(
    pt => new Vector2(modelToUI(pt.x - obstruction.position.x), modelToUI(pt.y - obstruction.position.y))
  )
  const geometry = new ExtrudeGeometry(new Shape(points), { depth: modelToUI(obstruction.height) })

  const mesh = new Mesh(geometry)
  mesh.position.set(
    modelToUI(obstruction.position.x),
    modelToUI(obstruction.position.y),
    modelToUI(obstruction.offset ?? 0)
  )
  mesh.rotation.set(0, 0, MathUtils.degToRad(obstruction.rotation.z))

  applyObstructionHeight(obstruction, roofs, mesh.position)

  mesh.name = `basic obstruction ${obstruction.id}`

  return mesh
}

function modelSize(object: Object3D): Vector3 {
  const box = new Box3()
  box.setFromObject(object)
  const vec = new Vector3()
  box.getSize(vec)
  return vec
}

function rescaleToFit(modelSize: Vector3, obstruction: Obstruction): Vector3 {
  const points = obstruction.positions.map(pt => new Vector3(modelToUI(pt.x), modelToUI(pt.y), modelToUI(pt.z)))
  const height = modelToUI(obstruction.height)

  const { width, length } = cornerPoints(points, height)

  const targetSize = new Vector3(width, length, height)

  return targetSize.clone().divide(modelSize)
}

async function loadScene(str: string) {
  const loader = new GLTFLoader()
  loader.setMeshoptDecoder(MeshoptDecoder)
  const gltf = await loader.loadAsync(new URL(`/src/assets/gltf/obstructions/${str}.glb`, import.meta.url).href)

  gltf.scene.traverse(child => {
    if (child instanceof Mesh) {
      for (const key in child.material) {
        if (child.material[key] instanceof Texture) {
          child.material[key] = null
        }
      }
    }
  })

  return gltf
}

const loadSceneMemoized = lruMemoize(loadScene, { maxSize: 50 })

function applyObstructionHeight(obstruction: Obstruction, roofs: Mesh[], position: Vector3) {
  if (obstruction.startLocation !== 'ceiling') {
    position.setZ(modelToUI(obstruction.offset ?? 0))
    return
  }

  let distance = Infinity

  const origin = new Vector3().copy(obstruction.position)
  const up = new Vector3(0, 0, 1)
  const raycaster = new Raycaster(origin, up)

  for (const roof of roofs.values()) {
    const intersections = raycaster.intersectObject(roof)
    for (const intersection of intersections) {
      if (intersection.distance <= distance) {
        distance = intersection.distance
        const obstructionHeight = obstruction.height + (obstruction.offset ?? 0)
        const height = modelToUI(intersection.point.z - obstructionHeight)
        position.setZ(height)
      }
    }
  }
}

async function modelObstructionMesh(obstruction: Obstruction, roofs: Mesh[]) {
  const gltf = await loadSceneMemoized(obstruction.obstructionType)

  const size = modelSize(gltf.scene)
  const scale = rescaleToFit(size, obstruction)

  const group = new Group()
  group.position.copy(vectorModelToUI(obstruction.position))
  group.scale.copy(scale)
  group.rotation.set(0, 0, MathUtils.degToRad(obstruction.rotation.z))
  group.add(gltf.scene.clone())

  applyObstructionHeight(obstruction, roofs, group.position)

  group.name = `model obstruction ${obstruction.obstructionType} ${obstruction.id}`

  return group
}

const getObstructionClones = async (obstructions: Record<string, Obstruction>, roofs: Mesh[]) => {
  return await Promise.all(
    Object.values(obstructions).map(async obstruction => {
      const isDetailedObstruction = obstruction.obstructionType !== 'basic'

      if (isDetailedObstruction) {
        try {
          return await modelObstructionMesh(obstruction, roofs)
        } catch (err) {
          throw new Error(`Failed to model obstruction mesh ${obstruction.obstructionType} ${obstruction.id}`, {
            cause: err,
          })
        }
      } else {
        return basicObstructionMesh(obstruction, roofs)
      }
    })
  )
}

export const getFacilityClone = async ({
  productPositions,
  obstructions,
  ceilings,
  roofMeshes,
  wallSegments,
  walls,
}: {
  productPositions: FormattedProducts
  obstructions: Record<string, Obstruction>
  ceilings: Record<string, Ceiling>
  wallSegments: Record<string, WallSegment>
  walls: Record<string, Wall>
  roofMeshes: Mesh[]
}): Promise<{ facilityClone: Object3D; locations: Vector[] }> => {
  const facilityClone = new Object3D()
  const wallClones = getWallClones(wallSegments, walls)
  const floorClones = getFloorClones(wallSegments, walls)
  // TODO: Revisit multi-room facilities. For now, using empty array.
  const locations = getSimulationPoints(wallSegments, walls, productPositions)
  const obstructionClones = await getObstructionClones(obstructions, roofMeshes)
  const ceilingClones = getCeilingClones(ceilings)
  const roofClones = roofMeshes.map(mesh => {
    const clone = mesh.clone()
    clone.scale.divideScalar(UNIT_CONVERSION_SCALAR)
    return clone
  })

  facilityClone.add(...wallClones)
  facilityClone.add(...floorClones)
  facilityClone.add(...roofClones)
  if (obstructionClones.length) facilityClone.add(...obstructionClones)
  if (ceilingClones.length) facilityClone.add(...ceilingClones)

  // TODO: how to dispatch this only on upload?
  // const isContainedFacility = !checkFacilityGaps(
  //   facilityClone,
  //   productPositions.map(({ unscaledPositionData }) => unscaledPositionData)
  // )
  // if (!isContainedFacility) {
  //   // dispatch alert
  // }

  // Scale to inches for acceptable CFD input
  facilityClone.scale.set(
    Units.nativeToInches(facilityClone.scale.x),
    Units.nativeToInches(facilityClone.scale.y),
    Units.nativeToInches(facilityClone.scale.z)
  )

  facilityClone.updateMatrixWorld()

  console.warn(locations)
  return { facilityClone, locations: locations.length <= 1 ? [] : locations }
}
